import { createAmbience, type Ambience } from "./ambience";
import type { ExhibitionRegistry, PublicExhibition } from "./contracts";
import { createFooter } from "./footer";
import { trackGlass, type GlassSurfaces } from "./glass";
import { languageOptionsHtml, parseLanguage, type Language, type UiCopy } from "./i18n";
import { revealOnScrollUp, type IndexBar } from "./index-bar";
import { createMotionToggle } from "./motion-toggle";
import {
  createCoda,
  createInterlude,
  createOpening,
  disposeOpening,
  splitWords,
} from "./opening";
import { createReveal, reconstructionUrl } from "./reveal";
import { DEFAULT_REGION, isOpen, REGIONS, regionOf, type RegionId } from "./regions";
import { animateStory, type Story } from "./scrollytelling";
import { createVeil, createWash } from "./veil";
import { createShowcase, roomsFor } from "./showcase";
import { createCollection } from "./collection-view";
import { objectsIn, photographsIn } from "./collection";

let activeGlass: GlassSurfaces | null = null;
let activeStory: Story | null = null;
let activeAmbience: Ambience | null = null;
let activeBar: IndexBar | null = null;

export function disposeBackdrop(): void {
  activeGlass?.dispose();
  activeGlass = null;
  activeBar?.dispose();
  activeBar = null;
  activeStory?.dispose();
  activeStory = null;
  // Leaving for an exhibition tears the index down. Without this the oscillators keep
  // running against a detached button, and the room tone follows you into the museum.
  activeAmbience?.dispose();
  activeAmbience = null;
  // The starfield holds a rAF loop and two observers against a canvas that is about to
  // be detached.
  disposeOpening();
}

export type ExhibitionMenuHandlers = {
  onLanguage: (language: Language) => void;
  onRegion: (region: RegionId) => void;
  onSelect: (slug: string) => void;
};

/**
 * The heritages, as a row of names under the brand.
 *
 * A dropdown would hide the fact that there is more than Saudi Arabia here, and that
 * breadth is half of what the page is arguing. So they are all visible: the one you
 * can walk is lit, the rest are dimmed and inert. A dimmed name is a roadmap; a
 * clickable name that opens an empty museum is a broken promise.
 */
function createRegionStrip(
  active: RegionId,
  copy: UiCopy,
  onRegion: (region: RegionId) => void,
): HTMLElement {
  const nav = document.createElement("nav");
  nav.className = "region-strip";
  nav.setAttribute("aria-label", copy.regionLabel);
  for (const region of REGIONS) {
    const name = copy.regionNames[region.id];
    if (!isOpen(region)) {
      const soon = document.createElement("span");
      soon.className = "region-strip__item region-strip__item--soon";
      soon.textContent = name;
      // The state has to reach a screen reader too, which cannot see the dimming.
      const note = document.createElement("span");
      note.className = "sr-only";
      note.textContent = ` — ${copy.regionSoon}`;
      soon.append(note);
      nav.append(soon);
      continue;
    }
    const button = document.createElement("button");
    button.type = "button";
    button.className = "region-strip__item";
    button.dataset.region = region.id;
    button.textContent = name;
    if (region.id === active) button.setAttribute("aria-current", "true");
    button.addEventListener("click", () => onRegion(region.id));
    nav.append(button);
  }
  return nav;
}

export function renderExhibitionMenu(
  root: HTMLElement,
  registry: ExhibitionRegistry,
  language: Language,
  region: RegionId,
  copy: UiCopy,
  handlers: ExhibitionMenuHandlers,
  focusSlug: string | null = null,
): void {
  // The header used to open the page with a title, a lede and a control strip, which
  // put the furniture of a website in front of the subject. It is now a floating bar
  // over the first scene: the page opens on the peninsula, not on its own chrome.
  root.innerHTML = `
    <main class="exhibition-index" aria-labelledby="exhibitions-title">
      <header class="index-bar">
        <h1 id="exhibitions-title" class="index-bar__brand">${copy.exhibitionsTitle}</h1>
        <div class="index-controls">
          <label class="language-picker">
            <span>${copy.languageLabel}</span>
            <select data-menu-language aria-label="${copy.languageLabel}">
              ${languageOptionsHtml(language)}
            </select>
          </label>
          <span data-motion-slot></span>
          <span data-ambience-slot></span>
        </div>
      </header>
      <div class="story" data-story></div>
      <section class="exhibition-grid" aria-live="polite"></section>
    </main>`;
  root.querySelector<HTMLSelectElement>("[data-menu-language]")?.addEventListener("change", (event) => {
    const selector = event.currentTarget as HTMLSelectElement;
    handlers.onLanguage(parseLanguage(selector.value));
  });
  // Last child of the bar, and full-width in CSS, so it wraps onto its own line under
  // the brand instead of competing with the controls for the first one.
  const bar = required<HTMLElement>(root, ".index-bar");
  bar.append(createRegionStrip(region, copy, handlers.onRegion));
  // Re-rendering in place (a language or motion change) detaches the old scroller and
  // its listener with it, but disposing first keeps that an implementation detail
  // rather than something this has to be right about.
  activeBar?.dispose();
  activeBar = revealOnScrollUp(required<HTMLElement>(root, ".exhibition-index"), bar);
  activeAmbience = createAmbience(copy);
  root.querySelector("[data-ambience-slot]")?.replaceWith(activeAmbience.element);
  // Re-render on change: a scene decides at construction whether to build a video at
  // all, so the switch cannot be applied to the page that is already standing.
  const motion = createMotionToggle(copy, () => {
    renderExhibitionMenu(root, registry, language, region, copy, handlers, focusSlug);
  });
  root.querySelector("[data-motion-slot]")?.replaceWith(motion.element);
  const grid = required<HTMLElement>(root, ".exhibition-grid");
  // Only this heritage's exhibitions, in registry order rather than the region's, so the
  // narrative sequence stays the one the curation decided on. An exhibition no region
  // claims falls to the default: rendering an empty museum because someone forgot a line
  // in the map would be a worse failure than filing it under the wrong flag, and
  // `tests/regions.test.ts` fails the build if a published slug is ever unclaimed.
  const exhibitions = registry.exhibitions.filter(
    (item) => (regionOf(item.slug) ?? DEFAULT_REGION) === region,
  );
  const rooms = roomsFor(region);
  // Exhibitions are no longer the only thing a heritage can be made of. Greece is
  // photographs and the geometry generated from them, with nothing built at all, so the
  // empty state has to mean "this region has nothing" rather than "no exhibitions" —
  // otherwise the most finished section on the site renders as a failure notice.
  const collected =
    rooms.length > 0 || photographsIn(region).length > 0 || objectsIn(region).length > 0;
  if (exhibitions.length === 0 && !collected) {
    const empty = document.createElement("p");
    empty.className = "panel exhibition-empty";
    empty.textContent = copy.emptyExhibitions;
    grid.append(empty);
    // The attribution obligation does not depend on anything being published, and an
    // empty index is still a page that names the studio.
    required<HTMLElement>(root, ".exhibition-index").append(createFooter(copy, region));
    return;
  }
  const story = required<HTMLElement>(root, "[data-story]");
  story.append(createOpening(copy, region));
  // The photographs, the rooms made from them and the objects made from them, in that
  // order, as one run. The rooms lead among the three-dimensional material: they answer
  // the question a visiting client actually has — whether their own building can be
  // walked this way — which a room modelled from a plan cannot.
  story.append(
    createCollection(copy, region, rooms.length > 0 ? createShowcase(copy, rooms) : null),
  );

  if (exhibitions.length === 0) {
    // Not the Saudi coda — that one closes the archive, its line is about the country
    // that grew around the five sites and its plate is a Riyadh timelapse, so appending
    // it here ended Greece on Saudi Arabia. A heritage gets its own closing plate or it
    // gets none, and `createCoda` returns null for the ones that have none rather than
    // leaving the page to stop mid-scroll.
    const ending = createCoda(copy, region);
    if (ending !== null) story.append(ending);
    finish(root, copy, region, focusSlug);
    return;
  }

  const archiveHeading = document.createElement("h2");
  archiveHeading.className = "story-archive-title";
  archiveHeading.dataset.reveal = "";
  archiveHeading.textContent = copy.showcaseArchiveTitle;
  story.append(archiveHeading);
  // Interludes fall inside the run, never at either end: one before the first chapter
  // would delay the subject, and one after the last would sit between the narrative and
  // the index, where the stone band already marks the change of register.
  const INTERLUDES: ReadonlyMap<number, "sky" | "stone"> = new Map([
    [2, "sky"],
    [4, "stone"],
  ]);
  exhibitions.forEach((exhibition, index) => {
    story.append(
      createChapter(exhibition, language, copy, handlers.onSelect, index + 1, exhibitions.length),
    );
    const interlude = INTERLUDES.get(index + 1);
    if (interlude !== undefined && index + 1 < exhibitions.length) {
      story.append(createInterlude(interlude, copy));
    }
  });

  const ending = createCoda(copy, region);
  if (ending !== null) story.append(ending);

  const indexHeading = document.createElement("h2");
  indexHeading.className = "story-index-title";
  indexHeading.dataset.reveal = "";
  indexHeading.textContent = copy.storyIndexTitle;
  grid.before(indexHeading);

  for (const exhibition of exhibitions) {
    const card = createCard(exhibition, language, copy, handlers.onSelect);
    grid.append(card);
  }
  finish(root, copy, region, focusSlug);
}

/**
 * Everything that has to happen once the story is built, whatever it was built out of.
 * A region with no exhibitions returns early and still needs all of it.
 */
function finish(
  root: HTMLElement,
  copy: UiCopy,
  region: RegionId,
  focusSlug: string | null,
): void {
  // One dust field for the whole index, fixed over the scrollport. Per-scene veils are
  // clipped to their own stage and show the join as a hard line at every transition.
  required<HTMLElement>(root, ".exhibition-index").append(createVeil());
  // The haze that carries every handover the dust no longer covers.
  required<HTMLElement>(root, ".exhibition-index").append(createWash());
  required<HTMLElement>(root, ".exhibition-index").append(createFooter(copy, region));
  activeStory = animateStory(required<HTMLElement>(root, ".exhibition-index"));
  // After the cards exist, so their rects can be measured.
  activeGlass = trackGlass(
    required<HTMLElement>(root, ".exhibition-index"),
    ".exhibition-card",
  );
  if (focusSlug !== null) {
    root.querySelector<HTMLButtonElement>(`button[data-exhibition-slug="${focusSlug}"]`)?.focus();
  }
}

export function renderUnknownExhibition(
  root: HTMLElement,
  copy: UiCopy,
  onBack: () => void,
): void {
  root.innerHTML = `
    <main class="exhibition-index exhibition-index--message">
      <section class="panel" role="alert">
        <p class="eyebrow">${copy.museumEyebrow}</p>
        <h1>${copy.notFoundTitle}</h1>
        <p>${copy.notFoundMessage}</p>
        <button class="primary" type="button" data-back>${copy.backToExhibitions}</button>
      </section>
    </main>`;
  required<HTMLButtonElement>(root, "[data-back]").addEventListener("click", onBack);
}

export function renderRegistryFailure(root: HTMLElement, copy: UiCopy, onRetry: () => void): void {
  root.innerHTML = `
    <main class="exhibition-index exhibition-index--message">
      <section class="panel" role="alert">
        <p class="eyebrow">${copy.fatalEyebrow}</p>
        <h1>${copy.fatalTitle}</h1>
        <p>${copy.genericFailure}</p>
        <button class="primary" type="button" data-retry>${copy.retry}</button>
      </section>
    </main>`;
  required<HTMLButtonElement>(root, "[data-retry]").addEventListener("click", onRetry);
}

/**
 * One full-bleed chapter: the exhibition's own photograph behind its title, thesis
 * and a way in. The image and the text move at different rates while scrolling, which
 * is what separates them into foreground and background.
 */
function createChapter(
  exhibition: PublicExhibition,
  language: Language,
  copy: UiCopy,
  onSelect: (slug: string) => void,
  position: number,
  total: number,
): HTMLElement {
  const localized = exhibition.localizations[language];
  const title = localized?.title ?? exhibition.title;
  const summary = localized?.summary ?? exhibition.summary;

  const section = document.createElement("section");
  section.className = "chapter";
  section.dataset.chapter = "";
  section.setAttribute("aria-labelledby", `chapter-${exhibition.slug}`);

  const media = document.createElement("div");
  media.className = "chapter__media";
  const photograph = publicAssetUrl(exhibition.cover.path).href;
  const reconstruction = reconstructionUrl(exhibition.slug);
  if (reconstruction === null) {
    const image = document.createElement("img");
    image.src = photograph;
    image.alt = "";
    // The first chapter is the page's opening image; the rest can wait for scroll.
    image.loading = position === 1 ? "eager" : "lazy";
    image.width = 1600;
    image.height = 1067;
    media.append(image);
  } else {
    media.classList.add("chapter__media--reveal");
    media.append(createReveal(photograph, copy, reconstruction));
  }

  const body = document.createElement("div");
  body.className = "chapter__body";

  const label = document.createElement("p");
  label.className = "chapter__label";
  label.dataset.reveal = "";
  label.textContent = copy.storyChapterLabel(position, total);

  const heading = document.createElement("h2");
  heading.id = `chapter-${exhibition.slug}`;
  heading.dataset.reveal = "";
  heading.append(splitWords(title));

  const description = document.createElement("p");
  description.dataset.reveal = "";
  description.textContent = summary;

  const meta = document.createElement("p");
  meta.className = "chapter__meta";
  meta.dataset.reveal = "";
  meta.textContent = copy.exhibitionCardMeta(exhibition.room_count, exhibition.artwork_count);

  const enter = document.createElement("button");
  enter.type = "button";
  enter.className = "primary";
  enter.dataset.reveal = "";
  enter.textContent = copy.openExhibition;
  enter.addEventListener("click", () => onSelect(exhibition.slug));

  // The open-air walkthrough, which is a different renderer on a page of its own. An
  // anchor rather than a button on purpose: it is a real address, so a view somebody
  // likes is a link they can send. It sits beside the exhibition rather than replacing
  // it while the two viewers still know different amounts about a room.
  const openAir = document.createElement("a");
  openAir.className = "chapter__aside";
  openAir.dataset.reveal = "";
  openAir.href = `museum.html?exhibition=${encodeURIComponent(exhibition.slug)}`;
  openAir.textContent = copy.walkOpenAir;

  body.append(label, heading, description, meta, enter, openAir);

  // The section is tall; the stage is one scrollport high and sticks to the top of it.
  // That is the whole effect — the scene holds still and is scrolled *through* rather
  // than past, so the photograph has time to be looked at instead of sliding by.
  const stage = document.createElement("div");
  stage.className = "chapter__stage";
  stage.append(media, body);
  section.append(stage);
  return section;
}

function createCard(
  exhibition: PublicExhibition,
  language: Language,
  copy: UiCopy,
  onSelect: (slug: string) => void,
): HTMLElement {
  const localized = exhibition.localizations[language];
  const title = localized?.title ?? exhibition.title;
  const summary = localized?.summary ?? exhibition.summary;
  const article = document.createElement("article");
  article.className = "exhibition-card";

  const image = document.createElement("img");
  image.src = publicAssetUrl(exhibition.cover.path).href;
  image.alt = "";
  image.loading = "lazy";
  image.width = 960;
  image.height = 540;

  const body = document.createElement("div");
  body.className = "exhibition-card__body";
  const heading = document.createElement("h2");
  heading.textContent = title;
  const description = document.createElement("p");
  description.textContent = summary;
  const metadata = document.createElement("p");
  metadata.className = "exhibition-card__meta";
  metadata.textContent = copy.exhibitionCardMeta(exhibition.room_count, exhibition.artwork_count);
  const button = document.createElement("button");
  button.type = "button";
  button.className = "primary";
  button.dataset.exhibitionSlug = exhibition.slug;
  button.textContent = copy.openExhibition;
  button.addEventListener("click", () => onSelect(exhibition.slug));
  article.addEventListener("click", (event) => {
    if (event.target !== button) button.click();
  });
  body.append(heading, description, metadata, button);
  article.append(image, body);
  return article;
}

function publicAssetUrl(path: string): URL {
  const base = new URL(import.meta.env.BASE_URL, window.location.href);
  const url = new URL(`generated/exhibitions/${path}`, base);
  if (url.origin !== window.location.origin) throw new Error("ASSET_ORIGIN_UNSAFE");
  return url;
}

function required<T extends Element>(root: ParentNode, selector: string): T {
  const element = root.querySelector<T>(selector);
  if (element === null) throw new Error(`UI_ELEMENT_MISSING: ${selector}`);
  return element;
}
